import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import { cn, cva, type VariantProps } from '@lib/utils';

const skeletonVariants = cva(
  [
    // Base styles
    'relative overflow-hidden',
    // Background - semantic color
    'bg-surface-raised',
    // CSS custom properties for wave animation
    '[--easing:var(--ease-elastic)]',
    '[--sk-color:#fff9]',
    '[--sk-to:translateX(100%)]',
    '[--sk-from:translateX(-100%)]',
    // RTL support
    'rtl:[--sk-to:translateX(-100%)]',
    'rtl:[--sk-from:translateX(100%)]',
    // Dark mode wave color
    'dark:[--sk-color:var(--color-dark-400)]',
  ],
  {
    variants: {
      animate: {
        true: 'before:absolute before:inset-0 before:animate-wave before:bg-[linear-gradient(90deg,transparent,var(--sk-color),transparent)]',
        false: '',
      },
    },
    defaultVariants: {
      animate: true,
    },
  }
);

type SkeletonProps = VariantProps<typeof skeletonVariants> & ComponentPropsWithoutRef<'div'>;

const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  ({ animate = true, className, ...rest }, ref) => {
    return <div ref={ref} className={cn(skeletonVariants({ animate }), className)} {...rest} />;
  }
);

Skeleton.displayName = 'Skeleton';

export { Skeleton };
export type { SkeletonProps };
